import {useCallback, useEffect, useState} from "react";
import {Wallet} from "lucide-react";
import {
  getAdminInfluencerPayout,
  listAdminInfluencerPayouts,
  updateAdminInfluencerPayout,
  type AdminInfluencerPayoutDetail,
  type AdminInfluencerPayoutRow
} from "@corporatesclub/shared";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Textarea} from "@/components/ui/textarea";
import {
  AdminAlert,
  AdminBadge,
  AdminFilterBar,
  AdminFilterField,
  AdminModernTable,
  AdminPageLayout,
  AdminPagination,
  AdminTabbedModal
} from "./ui";

export function InfluencerPayoutsView() {
  const [items, setItems] = useState<AdminInfluencerPayoutRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState<AdminInfluencerPayoutDetail | null>(null);
  const [note, setNote] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(() => {
    listAdminInfluencerPayouts({page, pageSize: 20, status: status || undefined})
      .then((r) => {
        setItems(r.items);
        setTotalPages(r.totalPages);
      })
      .catch((e: Error) => setErr(e.message));
  }, [page, status]);
  useEffect(() => { load(); }, [load]);

  async function openItem(id: number) {
    setOpenId(id);
    setModalOpen(true);
    const d = await getAdminInfluencerPayout(id);
    setDetail(d);
    setNote(d.adminNote || "");
  }

  async function setStatusAction(next: string) {
    if(!detail) return;
    try {
      const d = await updateAdminInfluencerPayout(detail.id, {status: next, adminNote: note});
      setDetail(d);
      load();
      adminToast.success(next === "approved" ? "Payout approved." : next === "rejected" ? "Payout rejected." : "Payout updated.");
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Could not update payout");
    }
  }

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title="Payouts"
      description="Influencer payout requests"
      breadcrumbs={[
        {label: "Dashboard", href: "/vadmin"},
        {label: "Influencers"},
        {label: "Payouts"}
      ]}
    >
      <AdminFilterBar
        onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
        onReset={() => { setStatus(""); setSearch(""); setPage(1); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search influencer..."
      >
        <AdminFilterField label="Status">
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </AdminFilterField>
      </AdminFilterBar>
      <AdminModernTable
        items={items}
        getId={(p) => p.id}
        getTitle={(p) => `${p.clientName} — ${p.amount} Lei`}
        getSubtitle={(p) => p.clientEmail}
        getMeta={(p) => (
          <>
            <AdminBadge variant={p.status === "approved" ? "success" : p.status === "rejected" ? "danger" : "warning"}>{p.status}</AdminBadge>
            <AdminBadge variant="outline">#{p.id}</AdminBadge>
            <AdminBadge variant="secondary">{p.createdAt}</AdminBadge>
          </>
        )}
        getSearchText={(p) => adminSearchText(p.id, p.clientName, p.clientEmail, p.amount, p.status, p.createdAt)}
        searchValue={search}
        onSearchChange={setSearch}
        clientFilter
        hideSearch
        selectedId={openId}
        onRowClick={(p) => openItem(p.id)}
        emptyTitle="No payout requests"
      />
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />

      {detail ? (
        <AdminTabbedModal
          open={modalOpen}
          onOpenChange={(o) => { setModalOpen(o); if(!o) { setDetail(null); setOpenId(null); } }}
          title={<>Payout <span className="tabular-nums">#{detail.id}</span></>}
          description={(
            <span className="admin-modal__meta">
              <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">{detail.clientName}</span>
              <span className="admin-modal__meta-chip admin-modal__meta-chip--accent">{detail.amount} Lei</span>
            </span>
          )}
          icon={Wallet}
          tabs={[
            {
              id: "overview",
              label: "Overview",
              content: (
                <div className="space-y-3">
                  <p><b>Who:</b> {detail.clientName} ({detail.clientEmail}) · client #{detail.clientId}</p>
                  <p><b>Phone:</b> {detail.clientPhone || "—"}</p>
                  <p><b>When:</b> {detail.createdAt}</p>
                  <p><b>Amount:</b> {detail.amount} Lei</p>
                  <p><b>Status:</b> <AdminBadge variant={detail.status === "approved" ? "success" : detail.status === "rejected" ? "danger" : "warning"}>{detail.status}</AdminBadge></p>
                  <p><b>Payment details:</b></p>
                  <pre className="whitespace-pre-wrap text-sm bg-muted/40 p-3 rounded-md">{detail.paymentDetails}</pre>
                  <p><b>Stats:</b> {detail.stats.ordersCount} orders · base {detail.stats.baseAmount} Lei · commission {detail.stats.commission} Lei</p>
                  <label className="block text-sm font-medium">Admin note</label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button variant="secondary" size="sm" onClick={() => setStatusAction("pending")}>Pending</Button>
                    <Button size="sm" onClick={() => setStatusAction("approved")}>Approve</Button>
                    <Button variant="destructive" size="sm" onClick={() => setStatusAction("rejected")}>Reject (unlock)</Button>
                  </div>
                </div>
              )
            },
            {
              id: "orders",
              label: "Orders",
              content: (
                <ul className="space-y-2">
                  {detail.orders.map((o) => (
                    <li key={o.earningId} className="border-b border-border/60 pb-2">
                      <span className="font-medium">Order #{o.orderId}</span>
                      {" · "}{o.voucherCode} · {o.commission} Lei ({o.percent}% of {o.baseAmount})
                      <div className="text-xs text-muted-foreground">{o.orderDate} · status {o.orderStatus} · total {o.orderTotal} Lei</div>
                    </li>
                  ))}
                  {!detail.orders.length ? <li className="text-sm text-muted-foreground">No linked orders (rejected unlock clears items).</li> : null}
                </ul>
              )
            }
          ]}
        />
      ) : null}
    </AdminPageLayout>
  );
}
